import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toValue, watchEffect } from 'vue';

import {
  getReviewsByReviewType,
  getReviewsByReviewTypeById,
  postReviewsByReviewTypeAuto,
  putReviewsByReviewTypeById,
  type ReviewItem,
  type ReviewType,
} from '../api';

export interface ReviewsPage {
  items: ReviewItem[];
  totalCount: number;
}

/**
 * One page of reviewable items of one type, e.g. AWS accounts awaiting review. minimumDays
 * filters out anything reviewed more recently than that, query searches name/reviewer, sort is the
 * API's comma-separated sort keys; the backend sorts, paginates and hands back the total matching
 * count in a response header.
 */
export function useReviews(
  reviewType: MaybeRefOrGetter<ReviewType>,
  minimumDays: MaybeRefOrGetter<number>,
  query: MaybeRefOrGetter<string>,
  page: MaybeRefOrGetter<number>,
  limit: MaybeRefOrGetter<number>,
  sort: MaybeRefOrGetter<string>
) {
  return useQuery({
    queryKey: ['reviews', reviewType, minimumDays, query, page, limit, sort],
    // Without it, a new page's key drops data (and so the total) to nothing while loading, and
    // the table clamps its page back to 1 against a page count of 1.
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ReviewsPage> => {
      const response = await getReviewsByReviewType<true>({
        path: { reviewType: toValue(reviewType) },
        query: {
          minimumDays: toValue(minimumDays),
          query: toValue(query) || undefined,
          page: toValue(page),
          limit: toValue(limit),
          sort: toValue(sort) || undefined,
        },
      });
      return { items: response.data, totalCount: Number(response.headers['x-total-count'] ?? response.data.length) };
    },
  });
}

// The backend can only resolve auto-approval per-account (aws) or per-org (github); core-metadata
// has no such natural grouping to walk, so the route doesn't accept it.
const autoReviewableTypes = ['aws', 'github'] as const;

export type AutoReviewType = (typeof autoReviewableTypes)[number];

export function isAutoReviewable(reviewType: ReviewType): reviewType is AutoReviewType {
  return (autoReviewableTypes as readonly ReviewType[]).includes(reviewType);
}

/**
 * Approves every overdue item whose group is already empty. Pass dryRun to preview what it would
 * approve without writing anything.
 */
export function useAutoReview(reviewType: MaybeRefOrGetter<AutoReviewType>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dryRun: boolean) =>
      (
        await postReviewsByReviewTypeAuto({
          path: { reviewType: toValue(reviewType) },
          query: { dryRun },
        })
      ).data,
    onSuccess: (_reviews, dryRun) => {
      if (dryRun) return;
      void queryClient.invalidateQueries({ queryKey: ['reviews', toValue(reviewType)] });
    },
  });
}

// The list endpoint hands back an id that's already encodeURIComponent'd (it can contain "/" and
// "#", e.g. "core-metadata%23heisenberg%2Fwalter" for the sk "core-metadata#heisenberg/walter"),
// meant to be dropped straight into a URL path segment. The generated client's path serializer
// doesn't know that though, it encodeURIComponent's every path param unconditionally, so passing
// the id through as-is would double-encode it on the wire and the backend's single
// decodeURIComponent wouldn't recover the real key. Decode once here so the client's own encode
// reproduces exactly the string the backend expects.
function decodeReviewId(id: string): string {
  return decodeURIComponent(id);
}

async function fetchReview(reviewType: ReviewType, id: string) {
  return (await getReviewsByReviewTypeById({ path: { reviewType, id: decodeReviewId(id) } })).data;
}

/** One reviewable item's detail, including the members it gates. */
export function useReview(reviewType: MaybeRefOrGetter<ReviewType>, id: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: ['review', reviewType, id],
    // Guards the review-stack view, which can start out with no id (a direct link with no
    // selection) rather than the always-present route param the detail view supplies.
    enabled: () => Boolean(toValue(id)),
    queryFn: () => fetchReview(toValue(reviewType), toValue(id)),
  });
}

/**
 * Warms the cache for the next few items in a bulk review stack, so advancing past one lands on
 * data that's already there instead of a loading flash. Shares useReview's cache entry (same
 * queryKey/queryFn), so a prefetched item just resolves instantly once its turn comes.
 */
export function usePrefetchUpcomingReviews(
  reviewType: MaybeRefOrGetter<ReviewType>,
  ids: MaybeRefOrGetter<string[]>,
  index: MaybeRefOrGetter<number>,
  count = 2
): void {
  const queryClient = useQueryClient();

  watchEffect(() => {
    const type = toValue(reviewType);
    const allIds = toValue(ids);
    const currentIndex = toValue(index);

    for (const id of allIds.slice(currentIndex + 1, currentIndex + 1 + count)) {
      void queryClient.prefetchQuery({
        queryKey: ['review', type, id],
        queryFn: () => fetchReview(type, id),
      });
    }
  });
}

/** Attests the current reviewer reviewed this item today, then refreshes the list and detail. */
export function useMarkReviewed(reviewType: MaybeRefOrGetter<ReviewType>, id: MaybeRefOrGetter<string>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (
        await putReviewsByReviewTypeById({
          path: { reviewType: toValue(reviewType), id: decodeReviewId(toValue(id)) },
        })
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['review', toValue(reviewType), toValue(id)] });
      void queryClient.invalidateQueries({ queryKey: ['reviews', toValue(reviewType)] });
    },
  });
}
